interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry<any>>();

let _currentUserId: string | null = null;

export function setCacheUserId(userId: string | null): void {
  if (_currentUserId && userId && _currentUserId !== userId) {
    cache.clear();
  }
  _currentUserId = userId;
}

function scopedKey(key: string): string {
  return _currentUserId ? `${_currentUserId}:${key}` : key;
}

let rateLimitCooldownUntil = 0;
let consecutiveRateLimits = 0;

export function isNylasDisabled(): boolean {
  return import.meta.env.VITE_NYLAS_ENABLED === 'false';
}

const MAX_REQUESTS_PER_HOUR = 40;
let hourlyRequestCount = 0;
let hourlyWindowStart = Date.now();

export function checkAndIncrementBudget(): boolean {
  const now = Date.now();
  if (now - hourlyWindowStart > 60 * 60 * 1000) {
    hourlyRequestCount = 0;
    hourlyWindowStart = now;
  }
  if (hourlyRequestCount >= MAX_REQUESTS_PER_HOUR) {
    return false;
  }
  hourlyRequestCount++;
  return true;
}

export function getCached<T>(key: string): T | null {
  const entry = cache.get(scopedKey(key));
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    cache.delete(scopedKey(key));
    return null;
  }
  return entry.data as T;
}

export function setCache<T>(key: string, data: T, ttlMs: number): void {
  cache.set(scopedKey(key), { data, expiresAt: Date.now() + ttlMs });
}

export function invalidateCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  const scoped = scopedKey(prefix);
  for (const key of cache.keys()) {
    if (key.startsWith(scoped)) {
      cache.delete(key);
    }
  }
}

export function isRateLimited(): boolean {
  return Date.now() < rateLimitCooldownUntil;
}

export function getRateLimitRemainingMs(): number {
  const remaining = rateLimitCooldownUntil - Date.now();
  return remaining > 0 ? remaining : 0;
}

export function getConsecutiveRateLimits(): number {
  return consecutiveRateLimits;
}

export function setRateLimitCooldown(durationMs?: number): void {
  consecutiveRateLimits++;
  const jitter = Math.random() * 3000;
  const effectiveDuration = durationMs
    ? durationMs + jitter
    : computeExponentialBackoff(consecutiveRateLimits);
  const newCooldown = Date.now() + effectiveDuration;
  if (newCooldown > rateLimitCooldownUntil) {
    rateLimitCooldownUntil = newCooldown;
  }
}

export function clearRateLimitCooldown(): void {
  rateLimitCooldownUntil = 0;
  consecutiveRateLimits = 0;
}

export function resetConsecutiveRateLimits(): void {
  consecutiveRateLimits = 0;
}

function computeExponentialBackoff(attempt: number): number {
  const baseDelayMs = 15000;
  const maxDelayMs = 300000;
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt - 1);
  const jitter = Math.random() * 5000;
  return Math.min(exponentialDelay + jitter, maxDelayMs);
}

export const CACHE_TTL = {
  GRANT_STATUS: 5 * 60 * 1000,
  EMAIL_LIST: 5 * 60 * 1000,
  EMAIL_SEARCH: 2 * 60 * 1000,
  CALENDAR_EVENTS: 5 * 60 * 1000,
  SINGLE_EMAIL: 5 * 60 * 1000,
} as const;
